/**
 * Les favoris du client : une liste qu'il compose lui-même.
 *
 * Demandé par une cliente (Gwendoline Michel) le 19/09/2026 : retrouver ses
 * articles habituels sans parcourir 1 100 références. Distinct de
 * « Recommander ces articles », qui rejoue UNE commande passée — ici la liste
 * est choisie, et elle dure.
 *
 * Le champ `favorite_products` existait depuis toujours dans le schéma et
 * n'était lu par personne : rien à migrer, tout à brancher.
 *
 * Écriture OPTIMISTE : au comptoir comme sur un téléphone en 4G, un cœur qui
 * attend l'aller-retour du serveur donne l'impression que le clic n'a pas
 * marché — et on reclique, ce qui le retire. On affiche donc tout de suite, et
 * on revient en arrière si le serveur refuse.
 *
 * SANS COMPTE (01/11/2026, « Zalando a des favoris sans compte ») : le cœur
 * est montré à tout le monde. Un visiteur garde sa liste dans CE navigateur
 * (`shop_favoris_invite`, comme le panier) ; à la connexion elle est AJOUTÉE à
 * celle du compte, jamais substituée — on ne perd ni l'une ni l'autre — puis
 * effacée du navigateur. À la déconnexion on retombe sur la liste du
 * navigateur, donc vide : sur un appareil partagé, les favoris d'un client ne
 * restent pas affichés au suivant.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useCustomer } from '../../contexts/CustomerContext';
import { customerAuthApi } from '../../api/ecommerce.api';
import { logger } from '../../utils/logger';

const CLE_INVITE = 'shop_favoris_invite';
/** Plusieurs écrans utilisent le hook (catalogue, fiche) : un cœur touché
 *  dans la fiche doit se voir sur la vignette derrière. */
const EVT_INVITE = 'favoris-invite-change';

function lireInvite(): string[] {
  try {
    const brut = JSON.parse(localStorage.getItem(CLE_INVITE) || '[]');
    return Array.isArray(brut) ? brut.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function ecrireInvite(ids: string[]) {
  try {
    if (ids.length) localStorage.setItem(CLE_INVITE, JSON.stringify(ids));
    else localStorage.removeItem(CLE_INVITE);
  } catch {
    /* navigation privée : la liste vit le temps de la page */
  }
  window.dispatchEvent(new Event(EVT_INVITE));
}

/** Une seule fusion à la fois : chaque écran monté verrait la liste du
 *  navigateur et l'enverrait de son côté. */
let fusionEnCours = false;

export function useFavoris() {
  const { customer, isAuthenticated, setFavoris: majCacheProfil } = useCustomer();
  const [favoris, setFavoris] = useState<string[]>([]);
  const [enregistrement, setEnregistrement] = useState(false);
  // `setFavoris` du contexte est recréé à chaque rendu du fournisseur : le
  // mettre en dépendance relancerait l'effet de fusion sans raison.
  const majCacheRef = useRef(majCacheProfil);
  majCacheRef.current = majCacheProfil;

  // Connecté : la liste arrive avec le profil, aucun appel supplémentaire au
  // chargement — sauf s'il reste une liste du navigateur à rattacher.
  useEffect(() => {
    if (!isAuthenticated) {
      setFavoris(lireInvite());
      return;
    }
    const depuisProfil = (customer as any)?.preferences?.favorite_products;
    const profil: string[] = Array.isArray(depuisProfil) ? depuisProfil : [];
    const invite = lireInvite();
    const ajouts = invite.filter((id) => !profil.includes(id));
    if (!ajouts.length) {
      if (invite.length) ecrireInvite([]);
      setFavoris(profil);
      return;
    }
    const fusion = [...profil, ...ajouts];
    setFavoris(fusion);
    if (fusionEnCours) return;
    fusionEnCours = true;
    customerAuthApi
      .updatePreferences({ favorite_products: fusion })
      .then(() => {
        ecrireInvite([]);
        majCacheRef.current(fusion);
      })
      .catch((e) => {
        // La liste du navigateur est gardée : on réessaiera à la prochaine visite.
        logger.error('[useFavoris] rattachement des favoris du navigateur échoué:', e);
      })
      .finally(() => {
        fusionEnCours = false;
      });
  }, [customer, isAuthenticated]);

  // Visiteur : suivre la liste du navigateur (autre écran, autre onglet).
  useEffect(() => {
    if (isAuthenticated) return;
    const relire = () => setFavoris(lireInvite());
    window.addEventListener(EVT_INVITE, relire);
    window.addEventListener('storage', relire);
    return () => {
      window.removeEventListener(EVT_INVITE, relire);
      window.removeEventListener('storage', relire);
    };
  }, [isAuthenticated]);

  const estFavori = useCallback((productId: string) => favoris.includes(productId), [favoris]);

  const basculer = useCallback(
    async (productId: string) => {
      if (!productId) return;
      const avant = favoris;
      const apres = avant.includes(productId)
        ? avant.filter((id) => id !== productId)
        : [...avant, productId];
      setFavoris(apres); // optimiste
      if (!isAuthenticated) {
        ecrireInvite(apres);
        return;
      }
      setEnregistrement(true);
      try {
        await customerAuthApi.updatePreferences({ favorite_products: apres });
        // Le profil n'est lu qu'au montage : sans cette mise a jour du cache,
        // la liste repartait vide des qu'on changeait de page.
        majCacheProfil(apres);
      } catch (e) {
        logger.error('[useFavoris] enregistrement échoué, retour en arrière:', e);
        setFavoris(avant); // le serveur a refusé : on ne ment pas à l'écran
      } finally {
        setEnregistrement(false);
      }
    },
    [favoris, isAuthenticated, majCacheProfil],
  );

  // `actif` reste pour les appelants : le cœur est désormais pour tous.
  return { favoris, estFavori, basculer, enregistrement, actif: true, connecte: isAuthenticated };
}
